// interfaces / constants
import {
  SHARE_EMAIL,
  SHARE_FACEBOOK,
  SHARE_LINK,
  SHARE_LINKEDIN,
  SHARE_NATIVE,
  SHARE_WHATSAPP
} from 'src/utils/reporting/events_tracking/events_tracking'

// helpers
import { withUTMParams } from 'src/components/share/lib/utils'
import { textResources } from 'src/lang/de'

type ShareCatalog = typeof textResources.share & Partial<{
  copyLink: string;
  linkedin: string;
  shareVia: (service: string) => string;
}>

const catalog: ShareCatalog = textResources.share

export const shareTexts = {
  copyLink: catalog.copyLink ?? 'Link kopieren',
  email: catalog.email,
  facebook: catalog.facebook,
  link: catalog.link,
  linkWasCopiedToClipboard: catalog.linkWasCopiedToClipboard,
  linkedin: catalog.linkedin ?? 'LinkedIn',
  share: catalog.share,
  shareVia: (service: string) => catalog.shareVia?.(service) ?? `Per ${service} teilen`,
  whatsapp: catalog.whatsapp
}

export type ShareTrackingAction = typeof SHARE_EMAIL
  | typeof SHARE_FACEBOOK
  | typeof SHARE_LINK
  | typeof SHARE_LINKEDIN
  | typeof SHARE_NATIVE
  | typeof SHARE_WHATSAPP;

export type ShareTargetName = 'whatsapp' | 'facebook' | 'linkedin' | 'email';

export interface IShareTarget {
  name: ShareTargetName;
  caption: string;
  label: string;
  href: string;
  mobileOnly: boolean;
  trackingAction: ShareTrackingAction;
}

export interface ISharePayload {
  title?: string | null;
  text?: string;
}

const joinLines = (...parts: Array<string | null | undefined>) =>
  encodeURIComponent(parts.filter(Boolean).join('\n'))

export const buildShareTargets = (
  absoluteUrl: string,
  utmSource: string,
  { title, text }: ISharePayload
): IShareTarget[] => {
  const urlFor = (medium: string) => withUTMParams(absoluteUrl, { medium, source: utmSource })

  return [
    {
      caption: shareTexts.whatsapp,
      href: `whatsapp://send?text=${joinLines(text ?? title, urlFor('whatsapp'))}`,
      label: shareTexts.shareVia(shareTexts.whatsapp),
      mobileOnly: true,
      name: 'whatsapp',
      trackingAction: SHARE_WHATSAPP
    },
    {
      caption: shareTexts.facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(urlFor('facebook'))}`,
      label: shareTexts.shareVia(shareTexts.facebook),
      mobileOnly: false,
      name: 'facebook',
      trackingAction: SHARE_FACEBOOK
    },
    {
      caption: shareTexts.linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(urlFor('linkedin'))}`,
      label: shareTexts.shareVia(shareTexts.linkedin),
      mobileOnly: false,
      name: 'linkedin',
      trackingAction: SHARE_LINKEDIN
    },
    {
      caption: shareTexts.email,
      href: `mailto:?subject=${encodeURIComponent(title ?? '')}&body=${joinLines(text, urlFor('email'))}`,
      label: shareTexts.shareVia(shareTexts.email),
      mobileOnly: false,
      name: 'email',
      trackingAction: SHARE_EMAIL
    }
  ]
}
